import React, { useState, useEffect, useMemo } from 'react';
import type { StreamStats, ServerStats } from '../../types';
import type { ChartPoint } from '../../hooks/useStats';
import { statsService } from '../../core/StatsService';
import TopBar from './TopBar';
import MainTimeline from './MainTimeline';
import RateControl from './groups/RateControl';
import VbvRc from './groups/VbvRc';
import EncodeOutput from './groups/EncodeOutput';
import Protection from './groups/Protection';
import NetworkFeedback from './groups/NetworkFeedback';
import DistributorSubs from './groups/DistributorSubs';
import BottomDrawer from './BottomDrawer';

interface DiagnosticDashboardProps {
  open: boolean;
  stats: StreamStats;
  chartData: ChartPoint[];
  onClose: () => void;
}

// 列出当前告警明细（hover chip 时显示）
// 返回每条告警的"短标签" + "详细描述"
function listAlerts(stats: StreamStats, sv: ServerStats | null): string[] {
  const alerts: string[] = [];
  if (stats.packetLoss > 1) alerts.push(`丢包 ${stats.packetLoss.toFixed(2)}%`);
  if (stats.rtt > 80) alerts.push(`RTT ${stats.rtt}ms`);
  if (stats.fps < 45) alerts.push(`帧率 ${stats.fps} FPS`);
  if (stats.freezeCount > 0) alerts.push(`冻结 ${stats.freezeCount} 次`);
  if (stats.latencyBreakdown.total > 100) {
    alerts.push(`总延迟 ${stats.latencyBreakdown.total.toFixed(0)}ms`);
  }
  if (sv?.vbv_capped) alerts.push('VBV 触顶');
  // events 优先用 events_lifetime（新名），fallback events_total
  const events = sv?.events_lifetime ?? sv?.events_total;
  if (events) {
    if (events.cpu_overload > 0) alerts.push(`CPU 过载 ${events.cpu_overload} 次`);
    if (events.encoder_recovery > 0) alerts.push(`编码器重建 ${events.encoder_recovery} 次`);
    if (events.sudden_drop > 0) alerts.push(`BWE 突变 ${events.sudden_drop} 次`);
  }
  return alerts;
}

const DiagnosticDashboard: React.FC<DiagnosticDashboardProps> = ({
  open,
  stats,
  chartData,
  onClose,
}) => {
  const [timeWindow, setTimeWindow] = useState<'60s' | '5min'>('60s');
  const [, setTick] = useState(0);

  // ESC 关闭
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // 1Hz 触发本组件 re-render（serverStats 是单例外部状态）
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [open]);

  const serverStats = statsService.serverStats;
  const alerts = useMemo(() => listAlerts(stats, serverStats), [stats, serverStats]);

  if (!open) return null;

  const handleExport = () => {
    // 占位：Stage 3 实现完整快照导出（含 5 分钟 ring buffer）
    // 当前先导出当前 stats + serverStats 的 JSON 快照
    const snapshot = {
      ts: new Date().toISOString(),
      time_window: timeWindow,
      stats,
      serverStats,
      chartData,
    };
    const json = JSON.stringify(snapshot, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diag-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* 半透明 backdrop（视频隐约可见） */}
      <div
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm transition-opacity animate-fade-in"
        onClick={onClose}
      />

      {/* Modal 主容器 */}
      <div className="fixed inset-4 z-[101] glass-high rounded-3xl shadow-4xl border border-white/10 flex flex-col overflow-hidden animate-slide-up">
        <TopBar
          stats={stats}
          serverStats={serverStats}
          timeWindow={timeWindow}
          onTimeWindowChange={setTimeWindow}
          alerts={alerts}
          onExport={handleExport}
          onClose={onClose}
        />

        {/* 主内容区（Stage 2C.2 已填：MainTimeline；2C.3-5 待填充：6 卡片 + 底部抽屉）*/}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-5">
          {/* 主时间线（Stage 2C.2 + 3D：根据 timeWindow 切片）*/}
          <MainTimeline data={timeWindow === '60s' ? chartData.slice(-60) : chartData} timeWindow={timeWindow} />

          {/* 6 个分组卡片（Stage 2C.3）*/}
          <div className="grid grid-cols-3 gap-4">
            <RateControl stats={stats} serverStats={serverStats} />
            <VbvRc serverStats={serverStats} />
            <EncodeOutput stats={stats} serverStats={serverStats} />
            <Protection serverStats={serverStats} />
            <NetworkFeedback stats={stats} serverStats={serverStats} />
            <DistributorSubs serverStats={serverStats} />
          </div>

          {/* 底部抽屉（Stage 2C.5）*/}
          <BottomDrawer stats={stats} serverStats={serverStats} />
        </div>
      </div>
    </>
  );
};

export default DiagnosticDashboard;
